<script lang="ts">
    import { fade } from 'svelte/transition';
    import { get } from 'svelte/store';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { Navigation } from '../../../utilities/Navigation';

    import {
        TacticalPlanApi,
        type TacticalPlanResponseType,
    } from '../../TacticalPlan/TacticalPlanApi';
    import { CollectionApi, type CollectionResponseType } from '../../Collections/CollectionApi';
    import { filterOnlyTacticalPlansForDay } from '../../../utilities/TacticalPlans';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import MessageNotFound from '../../../shared/Messages/MessageNotFound.svelte';
    import SelectTacticalPlan from '../../../shared/SelectTacticalPlan.svelte';
    import MessageDanger from '../../../shared/Messages/MessageDanger.svelte';
    import type { VehicleResponseType } from '../../Vehicles/types';
    import type { ScenarioResponseType } from '../ScenarioTypes';
    import ButtonSmallBlack from '../../../shared/ButtonSmallBlack.svelte';
    import { VehiclesApi } from '../../Vehicles/VehiclesApi';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import ScenarioOne from './ScenarioOne.svelte';
    import { debug, planning, tacticalPlanStore } from '../../../../stores/store';
    import { ScenarioApi } from '../ScenarioApi';
    import { api, customerSettings } from '../../../../stores/api';
    import ButtonMain from '../../../shared/ButtonMain.svelte';
    import TemplateInner from '../../../shared/TemplateInner.svelte';
    import { AssignmentApi, type AssignmentResponseType } from '../AssignmentApi';
    import { Assignment } from '../Assignment';
    import MessageInfo from '../../../shared/Messages/MessageInfo.svelte';
    import { features } from '../../../shared/Features/features';
    import { vehiclesManager } from '../../Vehicles/VehiclesApp/vehiclesManager';
    import { createTacticalPlan } from '../../TacticalPlan/createTacticalPlan';

    export let day: string;
    export let onlyLocked: boolean = false;

    let loading: boolean = true;
    let error: string = '';
    let vehicles: VehicleResponseType[] = [];
    let tacticalPlans: TacticalPlanResponseType[] = [];
    let collection: CollectionResponseType | null = null;
    let itemsTacticalPlans: { id: string; label: string }[] = [];
    let previousDay: string = day;
    let scenarios: ScenarioResponseType[] = [];

    let showNoTacticalPlan: boolean = false;
    let showScenarioOne: boolean = false;
    let selectedScenario: ScenarioResponseType | null = null;

    async function reloadScenario(
        day: string,
        collection: CollectionResponseType,
        tacticalPlans: TacticalPlanResponseType[]
    ) {
        loading = true;
        scenarios = await ScenarioApi.filter(
            get(api),
            new ApiFilterBuilder(null).filterByString('day', day)
        );
        itemsTacticalPlans = filterOnlyTacticalPlansForDay(tacticalPlans, day);

        let selectedTacticalPlanId: number | null = null;
        if (scenarios.length === 0) {
            // try to create scenario

            if (itemsTacticalPlans.length > 0) {
                selectedTacticalPlanId = parseInt(itemsTacticalPlans[0].id);
            }

            if (selectedTacticalPlanId === null) {
                console.debug('ScenarioList, No tactical plan found for this day of week');
                showNoTacticalPlan = true;
                loading = false;
                return;
            } else if (!onlyLocked) {
                const createScenario = async () => {
                    return ScenarioApi.create(get(api), {
                        day,
                        name: 'Scenario name',
                        planning_started: null,
                        collection_id: collection.id,
                        tactical_plan_id: selectedTacticalPlanId,
                        to_compare_only: false,
                        selected: true,
                    });
                };
                if ($debug) {
                    if (confirm('(debug mode) Create scenario?')) {
                        let scenario = await createScenario();
                        if (scenario) {
                            scenarios.push(scenario);
                        } else {
                            alert('Error creating scenario');
                        }
                    }
                } else {
                    let scenario = await createScenario();
                    if (scenario) {
                        scenarios.push(scenario);
                    } else {
                        alert('Error creating scenario');
                    }
                }
            }
        }
        if (scenarios.length >= 1) {
            selectedScenario = scenarios.filter(s => !s.to_compare_only)[0] ?? null;
            if (selectedScenario) {
                showNoTacticalPlan = false;
                $planning.currentScenarioId = selectedScenario.id;
                showScenarioOne = true;
            } else {
                console.error('ScenarioList, Selected scenario is null', scenarios);
                throw new Error('Selected scenario is null');
            }
        }
        loading = false;
    }

    function reloadData() {
        Promise.all([
            null, //VehiclesApi.getAll(get(api), null),
            TacticalPlanApi.getAll(get(api)),
            CollectionApi.getCollection(get(api)),
            $vehiclesManager.prepareData(),
        ])
            .then(response => {
                //vehicles = response[0];
                vehicles = $vehiclesManager.getItems(null).map((v) => v.get());
                tacticalPlans = response[1];
                collection = response[2];
                reloadScenario(day, collection, tacticalPlans);
            })
            .catch(err => {
                error = err;
                loading = false;
            });
    }

    onMount(() => {
        previousDay = day;
        reloadData();
    });

    $: {
        if (previousDay !== day && collection !== null) {
            previousDay = day;
            reloadData();
        }
    }

    $: {
        if ($tacticalPlanStore.forceReload) {
            $tacticalPlanStore.forceReload = false;
            reloadData();
        }
    }
</script>

{#if loading}
    <div in:fade={{ delay: 1000, duration: 500 }}>
        <TemplateInner>
            <Spinner />
        </TemplateInner>
    </div>
{:else}
    {#if error !== ''}
        <MessageDanger {error} />
    {/if}
    {#if showNoTacticalPlan}
        <div
            style="display: flex; justify-content: center; align-items: center;min-height: 200px; padding: 30px; flex-direction: column;"
        >
            <MessageNotFound text={$_('No tactical plan found for this day of week')} />
            <ButtonMain
                text={$_('Create tactical plan')}
                showIcon={false}
                on:click={() => {
                    // createTacticalPlan(get(api), []); -- dont create new tactical plan by default (maybe is needed only to check another week day in existing one)
                    Navigation.navigate('/tactical-plan');
                }}
            />
        </div>
    {:else if showScenarioOne && selectedScenario !== null}
        <ScenarioOne scenario={selectedScenario} allScenarios={scenarios} {vehicles} {onlyLocked} />
    {:else if onlyLocked}
        <TemplateInner>
            <MessageNotFound text={$_('Not found any jobs for this day')} />
        </TemplateInner>
    {:else}
        <MessageDanger message={$_('Something went wrong.')} />
    {/if}
{/if}

{#if $debug}
    {day}
    <!--{JSON.stringify(tacticalPlans)}-->
    {JSON.stringify(itemsTacticalPlans)}
    <!--{JSON.stringify(filterOnlyTacticalPlansForDay(tacticalPlans, day))}-->
    {$planning.currentScenarioId}
{/if}
